import { memo, useCallback, useMemo } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  OverlayTrigger,
  Row,
  Tooltip,
} from "react-bootstrap";
import { FaGlobe } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useGetPartners } from "../../api/queries/useGetPartners";

const LOGO_WIDTH = 120;
const LOGO_HEIGHT = 80;

const LOGO_IMG_STYLE: React.CSSProperties = {
  height: LOGO_HEIGHT,
  objectFit: "contain",
  width: LOGO_WIDTH,
};

const LINE_CLAMP_STYLE: React.CSSProperties = {
  WebkitLineClamp: 3,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const CARD_BODY_MIN_HEIGHT = 280;
const CARD_BODY_STYLE: React.CSSProperties = {
  minHeight: CARD_BODY_MIN_HEIGHT,
};

function PartnersInner() {
  const { t } = useTranslation();
  const { data } = useGetPartners({ pageSize: 50, page: 1 });
  const mergedPartners = useMemo(
    () =>
      (data?.partners.data ?? []).map((partner) => {
        const imageEntry = data?.partnerImageUrls?.find(
          (img) => img.partnerId === partner.id,
        );
        return {
          ...partner,
          ...imageEntry?.imageUrls,
        };
      }),
    [data?.partners.data, data?.partnerImageUrls],
  );

  const handleVisitWebsite = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <Container fluid className="page-content p-4 pt-5 pt-md-5">
      <div className="shadow-sm rounded bg-white">
        <div className="text-center bg-primary bg-gradient p-4 p-md-5 text-white">
          <h1 className="fw-bold mb-2 display-6">{t("partners.title")}</h1>
          <p className="mb-0 lead">{t("partners.subtitle")}</p>
        </div>

        <Row className="g-4 p-4">
          {mergedPartners.map((partner) => (
            <Col key={partner.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                className="h-100 shadow border-0 text-center d-flex flex-column"
                style={{
                  backgroundColor: partner.backgroundColour || "#ffffff",
                  color: partner.fontColour || "#000000",
                }}
              >
                <Card.Body
                  className="d-flex flex-column flex-grow-1 align-items-center p-4"
                  style={CARD_BODY_STYLE}
                >
                  <img
                    src={partner.Logo || "/connect-logo.png"}
                    alt={t("partners.logoAlt", {
                      name: partner.name || t("partners.fallbackName"),
                    })}
                    width={LOGO_WIDTH}
                    height={LOGO_HEIGHT}
                    className="img-fluid mb-3"
                    style={LOGO_IMG_STYLE}
                    loading="lazy"
                    decoding="async"
                  />

                  <h5 className="fw-bold mb-2">
                    {partner.name || t("partners.fallbackName")}
                  </h5>

                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-${partner.id}`}>
                        {partner.description ||
                          t("partners.fallbackDescription")}
                      </Tooltip>
                    }
                  >
                    <p
                      className="small text-truncate mb-3 w-100"
                      style={LINE_CLAMP_STYLE}
                    >
                      {partner.description || t("partners.fallbackDescription")}
                    </p>
                  </OverlayTrigger>

                  {partner.websiteUrl && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="mt-auto rounded-pill custom-outline-primary"
                      onClick={() => handleVisitWebsite(partner.websiteUrl)}
                    >
                      <FaGlobe className="me-2" />
                      {t("partners.visitWebsite")}
                    </Button>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </Container>
  );
}

PartnersInner.displayName = "Partners";

export default memo(PartnersInner);
